import { IDynamicImportFailure } from "./dynamic-import-failure.types";

/**
 * Key the tag is stored under. Registered via `Symbol.for` so a tag written by
 * one copy of this module is still readable by another — bundlers can duplicate
 * a module across chunks, and a local `Symbol()` would not match across copies.
 */
const DYNAMIC_IMPORT_FAILURE = Symbol.for("edgetier.dynamicImportFailure");

/**
 * Tag an error as having come from a dynamic import. The tag is non-enumerable and
 * symbol-keyed, so it stays out of `Object.keys` and `JSON.stringify` and does not
 * reach Sentry's serialisation, and it leaves `name` and `message` untouched.
 * Returns the same value so callers can `throw markDynamicImportFailure(error, …)`
 * and still rethrow the original instance.
 *
 * @param error The caught rejection value. Non-objects are returned untouched.
 * @param detail Context to attach for later reporting.
 * @returns The value passed in.
 */
export const markDynamicImportFailure = <TError>(error: TError, detail: IDynamicImportFailure = {}): TError => {
    if (typeof error !== "object" || error === null) {
        return error;
    }

    try {
        Object.defineProperty(error, DYNAMIC_IMPORT_FAILURE, {
            configurable: true,
            enumerable: false,
            value: detail,
            writable: true,
        });
    } catch {
        // A frozen or sealed error cannot be tagged. Classification then falls back
        // to message matching, which is the behaviour we had before tagging existed.
    }

    return error;
};

/**
 * Read the context recorded when a dynamic import failure was tagged.
 *
 * @param error The error to inspect.
 * @returns The recorded detail, or `undefined` if the error was never tagged.
 */
export const getDynamicImportFailure = (error: unknown): IDynamicImportFailure | undefined => {
    if (typeof error !== "object" || error === null) {
        return undefined;
    }

    const detail = (error as Record<symbol, unknown>)[DYNAMIC_IMPORT_FAILURE];

    return typeof detail === "object" && detail !== null ? (detail as IDynamicImportFailure) : undefined;
};

/**
 * Whether a failed import means the *bytes* were bad, as opposed to a bug in the
 * module's own code. Needed because such a failure carries no chunk wording and no
 * stack frames, so message matching cannot classify it (GAS-1043).
 *
 * Both arrive as a rejection from the same `import()`, so the tag alone cannot
 * tell them apart — but the error kind can. A module that fails to parse never
 * executed, and the engine reports that as a `SyntaxError`. A module that did
 * execute and threw reports whatever its code threw (`TypeError`,
 * `ReferenceError`, …), which is an application bug and should still be treated
 * as a crash.
 *
 * Requires the tag, so a `SyntaxError` from somewhere else entirely (a `JSON.parse`
 * in application code) is not mistaken for an asset problem. Use alongside
 * `isChunkLoadError`, which still covers failures thrown outside a tagged import
 * (a bare `React.lazy`, a Vite CSS preload).
 *
 * @param error The error to inspect.
 * @returns True if a tagged import failed because the module would not parse.
 */
export const isModuleParseFailure = (error: unknown): boolean =>
    typeof getDynamicImportFailure(error) !== "undefined" && (error as { name?: unknown }).name === "SyntaxError";
